/**
 * Preparación del mapa para capturar la lámina.
 *
 * Dos problemas concretos que resuelve este módulo, los dos vistos en producción:
 *
 * 1. **Recintos chicos salían grises.** `fitBounds` puede pedir un zoom más alto
 *    que el último nivel donde el proveedor TIENE fotografía. Esri responde esos
 *    niveles con una tesela que dice "Map data not yet available", y la lámina
 *    sale con un mosaico gris. Hay que capar el zoom al `maxNativeZoom` real de
 *    la capa activa.
 *
 * 2. **Se capturaba antes de que cargaran las teselas.** Esperar por tiempo es
 *    una apuesta: en una conexión lenta se captura a medias, y en una rápida se
 *    espera de más. Se espera por ESTADO, con un tope por si algo no llega.
 */

/** Último nivel de zoom donde la capa realmente tiene imagen. */
export function maxImageryZoom(layer) {
  const native = layer?.options?.maxNativeZoom;
  const max = layer?.options?.maxZoom;
  return Number.isFinite(native) ? native : Number.isFinite(max) ? max : 19;
}

/**
 * Encuadra el contenido dejando aire alrededor, sin pasarse del zoom donde hay
 * fotografía.
 *
 * El margen es proporcional y no 50 px fijos: la lámina reencuadra después el
 * recorte a la proporción de su hueco tomando imagen de alrededor, y si el
 * contenido llena el viewport no queda nada que tomar. Ver `fitCrop`.
 */
export function frameContent(map, bounds, container, layer) {
  const pad = Math.round(Math.min(container.offsetWidth, container.offsetHeight) * 0.14);
  map.fitBounds(bounds, { padding: [pad, pad], animate: false });

  const ceiling = maxImageryZoom(layer);
  if (map.getZoom() > ceiling) {
    // Bajar el zoom mantiene el centro y sólo agrega contexto alrededor: el
    // contenido sigue entrando entero, y ahora sobre fotografía real.
    map.setZoom(ceiling, { animate: false });
  }
}

/**
 * ¿Están cargadas todas las teselas visibles de la capa?
 * Leaflet marca cada tesela con `loaded` cuando termina; una tesela sin ese
 * campo todavía está en vuelo.
 */
export function tilesReady(layer) {
  if (!layer?._tiles) return false;
  const tiles = Object.values(layer._tiles);
  if (!tiles.length) return false;
  return tiles.every((tile) => !tile.current || tile.loaded);
}

/**
 * Espera a que la capa termine de cargar, por estado y no por reloj.
 *
 * El tope existe porque una tesela que falla nunca marca `loaded`, y sin él la
 * exportación quedaría colgada para siempre esperando una imagen que no llega.
 */
export function waitForTiles(layer, { timeoutMs = 8000, pollMs = 120 } = {}) {
  return new Promise((resolve) => {
    if (tilesReady(layer)) {
      resolve(true);
      return;
    }

    let done = false;
    const finish = (complete) => {
      if (done) return;
      done = true;
      clearInterval(poll);
      clearTimeout(cap);
      layer.off('load', onLoad);
      resolve(complete);
    };

    const onLoad = () => finish(true);
    layer.on('load', onLoad);

    // El evento `load` no vuelve a dispararse si las teselas ya estaban en
    // caché, así que además se sondea el estado.
    const poll = setInterval(() => {
      if (tilesReady(layer)) finish(true);
    }, pollMs);

    const cap = setTimeout(() => finish(false), timeoutMs);
  });
}
